// Voice mode's HTTP calls (BUILD 2 C2-6): the person's preferences and the
// company's speech policy. Cookie on the web and bearer in the native shells;
// every failure is a `SpeechHttpError` with the server's code (401, 402, 403,
// 429 by status), so the copy comes from ./issues.ts and ./error-copy.ts.
import { apiUrl, authHeaders } from '@/lib/shell';
import { SpeechHttpError } from '@/speech/contracts';
import { speechHttpError } from '@/speech/transport/http';
import type { SpeechPolicy, VoicePrefs } from './contracts';

export const VOICE_PREFERENCES_PATH = '/api/v1/speech/voice-preferences';
export const SPEECH_POLICY_PATH = '/api/v1/company/speech-policy';

export type VoiceFetch = (input: string, init: RequestInit) => Promise<Response>;
const defaultFetch: VoiceFetch = (input, init) => globalThis.fetch(input, init);

async function request<T>(
  path: string,
  method: 'GET' | 'PUT',
  body: unknown,
  fetchImpl: VoiceFetch = defaultFetch,
): Promise<T> {
  let response: Response;
  try {
    response = await fetchImpl(apiUrl(path), {
      method,
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        ...authHeaders(),
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch {
    throw new SpeechHttpError(0, 'request_failed');
  }
  if (!response.ok) throw await speechHttpError(response);
  return (await response.json()) as T;
}

/** PUT /api/v1/speech/voice-preferences with a partial `VoicePrefs` → the whole prefs. */
export async function putVoicePrefs(
  patch: Partial<VoicePrefs>,
  fetchImpl?: VoiceFetch,
): Promise<VoicePrefs> {
  const answer = await request<{ prefs: VoicePrefs }>(
    VOICE_PREFERENCES_PATH,
    'PUT',
    patch,
    fetchImpl,
  );
  return answer.prefs;
}

/** GET /api/v1/company/speech-policy. */
export async function fetchSpeechPolicy(fetchImpl?: VoiceFetch): Promise<SpeechPolicy> {
  const answer = await request<{ policy: SpeechPolicy }>(
    SPEECH_POLICY_PATH,
    'GET',
    undefined,
    fetchImpl,
  );
  return answer.policy;
}

/** PUT /api/v1/company/speech-policy (owner or admin) with a partial policy. */
export async function putSpeechPolicy(
  patch: Partial<SpeechPolicy>,
  fetchImpl?: VoiceFetch,
): Promise<SpeechPolicy> {
  const answer = await request<{ policy: SpeechPolicy }>(
    SPEECH_POLICY_PATH,
    'PUT',
    patch,
    fetchImpl,
  );
  return answer.policy;
}
